import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@phosphor-icons/react/dist/csr/Check';
import { PasswordIcon } from '@phosphor-icons/react/dist/csr/Password';
import { ShieldCheckIcon } from '@phosphor-icons/react/dist/csr/ShieldCheck';
import { ToggleLeftIcon } from '@phosphor-icons/react/dist/csr/ToggleLeft';
import { ToggleRightIcon } from '@phosphor-icons/react/dist/csr/ToggleRight';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { TemporaryPassword } from './TemporaryPassword';
import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
  VALIDATION_FAILED_CODE,
} from '../../lib/api/client';
import { usersApi, type AdminUser } from '../../lib/api/users';
import styles from './UserActions.module.css';

/**
 * The four row actions `UserListScreen` used to render inline, now owning
 * their own confirmation, failure copy and the one-time password.
 *
 * PERMISSION STRINGS AND ROW RULES ARE MOVED, NOT RE-DERIVED. Task 12's
 * review verified each against the server's `UserPolicy`, including two
 * traps a "tidier" rewrite would erase: deactivate is keyed on
 * `user.activate` (there is no `user.deactivate` string — one permission
 * covers both verbs, see `UserStatusController`), and `resetPassword` has
 * **no** self-refusal — `UserPolicy::resetPassword()` permits it against the
 * caller's own account, unlike the other three.
 *
 * ## Where a failure is shown, and why the four differ
 *
 * The three CONFIRMING actions (deactivate, reset password, reset two-step)
 * fail inside their own still-open `Dialog`, exactly as `ChallengeForm.tsx`
 * does: a `describe(error)` mapping `error.code` to translated copy, the
 * `requestId` beside it, and the live region mounted EMPTY from the first
 * frame the dialog opens on, not inserted after a rejection.
 *
 * Activate does not confirm — it takes nothing away — so it has no dialog to
 * fail inside. Its failure gets its OWN row-scoped notice, mounted empty
 * alongside the button, on the same terms `UserListScreen`'s now-removed
 * `reportActionFailure` used: `role="alert"`, never touching anything the
 * list's own loading state depends on. (That mechanism moved here rather
 * than staying split across two components — once every action's API call
 * lives in `UserActions`, a callback back into `UserListScreen` for exactly
 * one of the four would be a second, thinner copy of the same idea.)
 *
 * ## `onChanged`, not a refetch
 *
 * Every action that changes the row calls `onChanged(uuid, changes)` on
 * success instead of asking the parent to reload the whole list — a refetch
 * flashes the loading skeleton over four other rows to reflect a change to
 * one. Activate and deactivate get the full `AdminUser` the API returns and
 * pass it straight through. Reset-MFA's endpoint answers `204 No Content`
 * (nothing to merge from the response), so the change it reports is the one
 * the server's own handler makes unconditionally: clearing `mfa_enabled` and
 * the account-level `mfa_required` flag (`UserMfaResetController`). Reset
 * password returns only the one-time secret — nothing on the row changes
 * that this table renders, so it calls no `onChanged` at all.
 *
 * ## The in-flight guard
 *
 * `inFlight` is a ref, not the `submitting` state it runs alongside. Two
 * clicks inside one React batch both read `submitting` as it was before
 * either — `Button`'s own busy prop cannot have re-rendered between them —
 * so only a synchronous, immediately-written ref can stop the second call
 * before it reaches the network. Same shape as `MfaEnrolmentScreen.tsx`.
 * Shared across all four actions: a caller who fires two different actions
 * on the same row before the first settles gets the same one-at-a-time rule.
 */

export interface UserActionsProps {
  user: AdminUser;
  permissions: readonly string[];
  isSelf: boolean;
  /**
   * A row changed. `changes` is merged into the row by the parent, so the
   * list updates without a refetch.
   */
  onChanged: (uuid: string, changes: Partial<AdminUser>) => void;
}

/**
 * `ApiExceptionRenderer::STATUSES` (new-fs-helpline-api) — confirmed by
 * reading the renderer rather than assumed: 409 is `conflict`, 403 is
 * `forbidden`. Neither has an exported constant in `client.ts` yet; no
 * earlier screen has branched on them.
 */
const CONFLICT_CODE = 'conflict';
const FORBIDDEN_CODE = 'forbidden';

type Pending = 'deactivate' | 'resetPassword' | 'resetMfa';

export function UserActions({ user, permissions, isSelf, onChanged }: UserActionsProps) {
  const { t } = useTranslation();

  function can(permission: string): boolean {
    return permissions.includes(permission);
  }

  const [pending, setPending] = useState<Pending | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);

  /**
   * The reset dialog's OPTIONAL password field, and its own field-level
   * error.
   *
   * EMPTY IS NOT A VALUE — it is the request to generate one, which is what
   * this dialog did for its whole life before the field existed. Trimming
   * is deliberate: a stray space typed before a value the administrator
   * will read aloud is a password nobody can reproduce, and the server
   * would accept it silently.
   *
   * `passwordError` is separate from `failure` beside it. `failure` is the
   * dialog-level alert for "the call did not go through"; a rejected
   * password is not that — it is a fix to make in the field, so it renders
   * on the field, and the two never overwrite each other.
   */
  const [chosenPassword, setChosenPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [activateBusy, setActivateBusy] = useState(false);
  const [activateFailure, setActivateFailure] = useState<string | null>(null);
  const [activateReference, setActivateReference] = useState<string | null>(null);

  /** See the component docblock — a ref because `submitting` cannot be. */
  const inFlight = useRef(false);

  /**
   * Shared by all three confirming dialogs plus Activate — only one
   * confirming dialog is ever open at a time, so one `failure`/`reference`
   * pair is enough for those three, and a fresh open clears whatever the
   * last one left.
   *
   * `action` scopes the CONFLICT_CODE arm. A 409 means "a role held by this
   * account requires two-step verification" for exactly ONE of the four
   * actions this component offers — reset-MFA, the endpoint whose own 409 is
   * defined that way (`UserMfaResetController`). Mapping it to that copy for
   * every action was a defect waiting to surface: the day `deactivate` (or
   * activate, or resetPassword) grows a 409 of its own for an unrelated
   * reason — last administrator, open cases, anything — the administrator
   * reading this copy would be told about two-step verification. The other
   * three fall through to the generic `actionFailed` copy instead.
   */
  function describe(error: ApiError, action: Pending | 'activate'): string {
    switch (error.code) {
      case CONFLICT_CODE:
        return action === 'resetMfa' ? t('admin.mfaRoleRequired') : t('admin.actionFailed');
      case FORBIDDEN_CODE:
        return t('admin.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('admin.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('admin.actionFailed');
    }
  }

  /**
   * The failure region every action below renders — extracted so the three
   * confirming dialogs stop carrying the same 11-line block verbatim, and so
   * the reset-password path (previously exercised by no test at all) shares
   * the exact same code the covered paths do rather than a copy that could
   * quietly drift from them.
   *
   * `testId` is NOT the same literal for every caller, on purpose. The three
   * confirming dialogs (`pending` is one value at a time, and `Dialog`
   * unmounts its children entirely when `open` is false) can safely share
   * one — at most one of those three is ever mounted. Activate's own notice
   * is NOT gated by `pending` at all; it is mounted whenever `canActivate`
   * is true, independent of whether a confirming dialog is open beside it.
   * An inactive row with a reset-MFA-eligible state can show BOTH at once,
   * and a shared testid there is a live Playwright strict-mode violation
   * waiting for that combination — not a hypothetical one, since `canActivate`
   * and `canResetMfa` are independent row rules.
   */
  function renderFailure(
    boxClassName: string | undefined,
    textClassName: string | undefined,
    failureText: string | null,
    referenceText: string | null,
    testId: string,
  ): ReactNode {
    return (
      <div className={boxClassName} role="alert" aria-atomic="true">
        {failureText === null ? null : <span className={textClassName}>{failureText}</span>}
        {referenceText === null ? null : (
          <span className={styles['reference']}>
            {t('auth.supportReference')}{' '}
            <bdi dir="ltr" data-testid={testId}>
              {referenceText}
            </bdi>
          </span>
        )}
      </div>
    );
  }

  function openDialog(action: Pending): void {
    setPending(action);
    setFailure(null);
    setReference(null);
    // A value left behind from a previous open would be silently applied to
    // a DIFFERENT account the next time this dialog appears.
    setChosenPassword('');
    setPasswordError(null);
  }

  /** Cancel, Escape and the close button all land here. Cancel must not act. */
  function closeDialog(): void {
    setPending(null);
    setFailure(null);
    setReference(null);
    setChosenPassword('');
    setPasswordError(null);
  }

  async function activate(): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setActivateBusy(true);
    setActivateFailure(null);
    setActivateReference(null);

    try {
      const updated = await usersApi.activate(user.uuid);
      onChanged(user.uuid, updated);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setActivateFailure(describe(error, 'activate'));
      setActivateReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setActivateBusy(false);
    }
  }

  async function confirmDeactivate(): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setReference(null);

    try {
      const updated = await usersApi.deactivate(user.uuid);
      onChanged(user.uuid, updated);
      closeDialog();
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      // NOT closeDialog(): the administrator is looking at this dialog, and
      // closing it on failure means they watch it vanish with nothing to
      // show for why. `pending` stays as it is; only the alert fills in.
      setFailure(describe(error, 'deactivate'));
      setReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  async function confirmResetPassword(): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setReference(null);
    setPasswordError(null);

    const chosen = chosenPassword.trim();

    try {
      // `undefined`, not `''` — see `usersApi.resetPassword`. Empty means
      // generate, and the server rejects a present-but-empty key.
      const { temporary_password: password } = await usersApi.resetPassword(
        user.uuid,
        chosen === '' ? undefined : chosen,
      );
      setPending(null);
      setTemporaryPassword(password);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;

      /*
       * A rejected password renders ON THE FIELD, not in the dialog's
       * alert. The server is the only thing that knows the rule (twelve
       * characters, no composition requirements — `ChangePasswordRequest`'s
       * floor), so this maps its answer rather than re-deriving it here and
       * risking a client rule that disagrees with the one actually enforced.
       *
       * The `?? ` fallback matters: a `validation_failed` whose `details`
       * names some OTHER field, or nothing at all, must still say something.
       * Falling through to `describe()` there would leave a 422 showing the
       * generic "action failed" copy with no clue which field to fix.
       */
      if (error.code === VALIDATION_FAILED_CODE) {
        setPasswordError(error.details['password']?.[0] ?? t('admin.passwordRejected'));
      } else {
        setFailure(describe(error, 'resetPassword'));
        setReference(error.requestId ?? null);
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  async function confirmResetMfa(): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setReference(null);

    try {
      await usersApi.resetMfa(user.uuid);
      // The endpoint answers 204 — see the component docblock for why this
      // merge is written out rather than taken from a response body.
      onChanged(user.uuid, { mfa_enabled: false, mfa_required: false });
      closeDialog();
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setFailure(describe(error, 'resetMfa'));
      setReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  /**
   * Shown once, per `TemporaryPassword`'s own contract. Routed through here
   * (rather than `TemporaryPassword` clearing its own prop) so Escape and the
   * dialog's close button discard it exactly as `onAcknowledge` does — there
   * is no second way out of this dialog that leaves the value behind.
   */
  function dismissTemporaryPassword(): void {
    setTemporaryPassword(null);
  }

  // No `!isSelf` here. `UserPolicy::activate()` permits self-activation ON
  // PURPOSE (its own docblock: "Refusing it would be a rule copied rather
  // than applied"), and a `!isSelf` conjunct here would refuse something the
  // server allows. It was also unreachable in practice either way: the
  // `active` middleware means a caller's own row is always active, so
  // `!user.is_active` below already excludes the one row this would have
  // mattered for.
  const canActivate = can('user.activate') && !user.is_active;
  const canDeactivate = can('user.activate') && !isSelf && user.is_active;
  // No `!isSelf` here — see the component docblock's first paragraph. Adding
  // one would refuse an administrator resetting their OWN password, which
  // the server permits.
  const canResetPassword = can('user.password.reset');
  // Offered when there is EITHER a factor to clear OR a requirement to
  // lift — not `mfa_enabled` alone. An account with `mfa_required` set and
  // no enrolled factor is answered `202 enrolment_required` at login and
  // cannot get past it: enrolment sits behind `auth:sanctum`, and there is
  // no session to reach it with. This endpoint is the only thing that opens
  // that dead end, so gating it on `mfa_enabled` alone hid the control from
  // exactly the accounts that needed it — a defect found in real-browser
  // verification, not a hypothetical. See `UserListScreen.tsx`'s docblock
  // for the fuller account of why `mfa_enabled` alone was wrong.
  const canResetMfa = can('user.mfa.reset') && !isSelf && (user.mfa_enabled || user.mfa_required);

  if (!canActivate && !canDeactivate && !canResetPassword && !canResetMfa) {
    return null;
  }

  return (
    <div className={styles['actions']}>
      {canActivate ? (
        <Button
          icon={<ToggleRightIcon size={16} />}
          variant="ghost"
          loading={activateBusy}
          onClick={() => {
            void activate();
          }}
        >
          {t('admin.activate')}
        </Button>
      ) : null}

      {canDeactivate ? (
        <Button icon={<ToggleLeftIcon size={16} />} variant="ghost" onClick={() => openDialog('deactivate')}>
          {t('admin.deactivate')}
        </Button>
      ) : null}

      {canResetPassword ? (
        <Button icon={<PasswordIcon size={16} />} variant="ghost" onClick={() => openDialog('resetPassword')}>
          {t('admin.resetPassword')}
        </Button>
      ) : null}

      {canResetMfa ? (
        <Button icon={<ShieldCheckIcon size={16} />} variant="ghost" onClick={() => openDialog('resetMfa')}>
          {t('admin.resetMfa')}
        </Button>
      ) : null}

      {/*
        Activate's own failure — no dialog, so it cannot fail inside one. See
        the component docblock. Mounted empty from the first frame, exactly
        like the dialog alerts below.
      */}
      {canActivate
        ? renderFailure(
            styles['notice'],
            styles['noticeText'],
            activateFailure,
            activateReference,
            // Distinct from the three dialogs' shared testid below —
            // deliberately. This notice is mounted whenever `canActivate` is
            // true, independent of `pending`, so it can be on screen at the
            // same time as an open dialog's own failure region (an inactive,
            // reset-MFA-eligible row has both controls at once). Sharing one
            // testid across both would make that combination a Playwright
            // strict-mode violation the first time it actually happened.
            'userActionsActivateRequestId',
          )
        : null}

      <Dialog
        open={pending === 'deactivate'}
        destructive
        title={t('admin.confirmDeactivate', { fullName: user.full_name })}
        onClose={closeDialog}
        actions={
          <>
            <Button
              icon={<CheckIcon size={16} />}
              variant="destructive"
              loading={submitting}
              onClick={() => {
                void confirmDeactivate();
              }}
            >
              {t('admin.confirm')}
            </Button>
            <Button icon={<XIcon size={16} />} variant="ghost" onClick={closeDialog}>
              {t('admin.cancel')}
            </Button>
          </>
        }
      >
        <p className={styles['body']}>
          {t('admin.confirmDeactivateBody', { fullName: user.full_name })}{' '}
          <bdi dir="ltr">{user.username}</bdi>
        </p>

        {renderFailure(styles['failure'], styles['failureText'], failure, reference, 'userActionsRequestId')}
      </Dialog>

      <Dialog
        open={pending === 'resetPassword'}
        destructive
        title={t('admin.confirmResetPassword', { fullName: user.full_name })}
        onClose={closeDialog}
        actions={
          <>
            <Button
              icon={<CheckIcon size={16} />}
              loading={submitting}
              onClick={() => {
                void confirmResetPassword();
              }}
            >
              {t('admin.confirm')}
            </Button>
            <Button icon={<XIcon size={16} />} variant="ghost" onClick={closeDialog}>
              {t('admin.cancel')}
            </Button>
          </>
        }
      >
        <p className={styles['body']}>
          {t('admin.confirmResetPasswordBody', { fullName: user.full_name })}{' '}
          <bdi dir="ltr">{user.username}</bdi>
        </p>

        {/*
          OPTIONAL. Left blank, this dialog behaves exactly as it always has
          and the server generates the password.

          `type="text"`, NOT `type="password"`. The administrator has to read
          this value out to the account holder, and a masked field they
          cannot check is how a typo becomes a password nobody knows — the
          failure `ChangePasswordRequest` uses a confirmation field to avoid.
          Nothing is concealed by masking it either: the very next dialog
          displays the value in plain text on purpose.
        */}
        <Input
          label={t('admin.chosenPasswordLabel')}
          hint={t('admin.chosenPasswordHint')}
          width="fill"
          type="text"
          dir="ltr"
          autoComplete="off"
          spellCheck={false}
          value={chosenPassword}
          error={passwordError ?? undefined}
          onChange={(event) => {
            setChosenPassword(event.target.value);
          }}
        />

        {renderFailure(styles['failure'], styles['failureText'], failure, reference, 'userActionsRequestId')}
      </Dialog>

      <Dialog
        open={pending === 'resetMfa'}
        destructive
        title={t('admin.confirmResetMfa', { fullName: user.full_name })}
        onClose={closeDialog}
        actions={
          <>
            <Button
              icon={<CheckIcon size={16} />}
              loading={submitting}
              onClick={() => {
                void confirmResetMfa();
              }}
            >
              {t('admin.confirm')}
            </Button>
            <Button icon={<XIcon size={16} />} variant="ghost" onClick={closeDialog}>
              {t('admin.cancel')}
            </Button>
          </>
        }
      >
        <p className={styles['body']}>
          {t('admin.confirmResetMfaBody', { fullName: user.full_name })}{' '}
          <bdi dir="ltr">{user.username}</bdi>
        </p>

        {renderFailure(styles['failure'], styles['failureText'], failure, reference, 'userActionsRequestId')}
      </Dialog>

      <Dialog
        open={temporaryPassword !== null}
        destructive
        title={t('admin.temporaryPasswordTitle')}
        onClose={dismissTemporaryPassword}
      >
        {temporaryPassword === null ? null : (
          <TemporaryPassword
            password={temporaryPassword}
            fullName={user.full_name}
            username={user.username}
            onAcknowledge={dismissTemporaryPassword}
          />
        )}
      </Dialog>
    </div>
  );
}
